"use client";

/**
 * Live figures in the Web Index menu footer.
 *
 * WHAT IT MAY SHOW, AND WHY IT IS NOT THE OBVIOUS NUMBER. The tempting figure for a nav is "N
 * pages indexed", and that one is deliberately withheld: `web-index-hero-stats.tsx` records that
 * the page total "is no longer printed" while the index is being built, and the rewrites in
 * next.config.ts state the rule twice — ratios are published, the totals they came from are not.
 * So this shows the collection count (already public on /collections) and storage per stored page
 * (already public on /how-the-index-works and /collections). Both are figures the site stands
 * behind elsewhere; the menu is not the place to start publishing something new.
 *
 * FETCHED LAZILY, AND ONCE. The nav renders on every page, so a fetch at mount would cost a
 * request per page view for something nobody has looked at yet. This loads on the first menu open
 * and caches at module scope for the rest of the session — open the menu ten times, one request.
 *
 * FAILS TO NOTHING. If the index is unreachable the figures simply do not render and the footer
 * keeps its link. A nav that shows a dash where a number should be looks broken; a nav that shows
 * one fewer thing does not.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { Activity, ArrowUpRight } from "lucide-react";
import { fmtBytes } from "@/lib/format-stat";

type Snapshot = { collections: number | null; perPage: string | null };

/** Session cache. The figures move on a scale of minutes; refetching per open buys nothing. */
let cached: Snapshot | null = null;
let inflight: Promise<Snapshot | null> | null = null;

async function load(): Promise<Snapshot | null> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const r = await fetch("/api/index/storage", { cache: "no-store" });
      if (!r.ok) return null;
      const j = (await r.json()) as {
        collections?: { collection?: string }[];
        web?: { bytes_per_page_disk?: number; bytes_per_page?: number };
      };

      /* Same exclusion the server-side count uses: the two `askfinz_site_index*` collections are
         askFinz's index OF ITSELF, not a library of the web, and counting them inflates the claim. */
      const cols = Array.isArray(j.collections)
        ? j.collections.filter((c) => !String(c?.collection ?? "").startsWith("askfinz_site_index")).length
        : 0;

      const bytes = j.web?.bytes_per_page_disk ?? j.web?.bytes_per_page;
      cached = {
        collections: cols > 0 ? cols : null,
        perPage: typeof bytes === "number" && bytes > 0 ? fmtBytes(bytes) : null,
      };
      return cached;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function NavLiveStats({ active }: { active: boolean }) {
  const [snap, setSnap] = useState<Snapshot | null>(cached);

  useEffect(() => {
    if (!active || snap) return;
    let on = true;
    load().then((s) => {
      if (on && s) setSnap(s);
    });
    return () => {
      on = false;
    };
  }, [active, snap]);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <Link
        href="https://askfinz.ai/indexing_in_progress"
        target="_blank"
        rel="noopener noreferrer"
        role="menuitem"
        className="group inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--accent))] transition-colors hover:opacity-80"
      >
        <span className="relative flex size-2">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-[hsl(var(--accent))] opacity-60" />
          <span className="relative inline-flex size-2 rounded-full bg-[hsl(var(--accent))]" />
        </span>
        Watch it indexing, right now
        <ArrowUpRight className="h-3 w-3 opacity-50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>

      {/* Renders only once real figures arrive — never a placeholder dash. */}
      {snap?.collections != null && (
        <span className="inline-flex items-center gap-1.5 text-[12px] text-foreground/55">
          <Activity className="h-3.5 w-3.5 opacity-50" aria-hidden />
          <span className="font-mono tabular-num text-foreground/80">{snap.collections}</span>
          collections held
        </span>
      )}
      {snap?.perPage && (
        <span className="inline-flex items-center gap-1.5 text-[12px] text-foreground/55">
          <span className="font-mono tabular-num text-foreground/80">{snap.perPage}</span>
          of storage per page
        </span>
      )}
    </div>
  );
}
